import React, { useRef, useState } from "react";
import { Section } from "./primitives";
import { Switch } from "./ui/switch";
import useMediaQuery from "../hooks/useMediaQuery";
import {
  screens,
  assessData,
  watchData,
  forgeData,
  provisionData,
} from "../mock/inferraData";

const C = {
  card: "#16221c",
  inner: "#101a15",
  line: "#26362d",
  line2: "#1f2c25",
  text: "#e7ece8",
  mute: "#9aa8a0",
  green: "#7ccbb5",
  amber: "#e3a54e",
  red: "#ee8b72",
};

const money = (n) =>
  n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/* ---------- shared controls ---------- */

function DarkSlider({ value, min, max, onChange }) {
  const ref = useRef(null);
  const pct = ((value - min) / (max - min)) * 100;

  const setFromX = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    onChange(Math.round(min + p * (max - min)));
  };

  const onDown = (e) => {
    e.preventDefault();
    setFromX(e.clientX);
    const move = (ev) => setFromX(ev.clientX);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div
      ref={ref}
      onPointerDown={onDown}
      style={{ position: "relative", height: 22, display: "flex", alignItems: "center", cursor: "pointer", touchAction: "none" }}
    >
      <div style={{ height: 6, width: "100%", borderRadius: 3, background: C.line2 }}>
        <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: C.green }} />
      </div>
      <div
        style={{
          position: "absolute",
          left: `calc(${pct}% - 9px)`,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#eef3f0",
          border: `3px solid ${C.green}`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.45)",
        }}
      />
    </div>
  );
}

function Segmented({ options, value, onChange, m }) {
  return (
    <div style={{ display: "inline-flex", flexWrap: "wrap", gap: 3, padding: 3, borderRadius: 10, background: C.inner, border: `1px solid ${C.line}` }}>
      {options.map((o) => {
        const active = o === value;
        return (
          <button
            key={o}
            onClick={() => onChange(o)}
            style={{
              border: "none",
              cursor: "pointer",
              padding: m ? "6px 10px" : "6px 13px",
              borderRadius: 7,
              fontSize: m ? 11.5 : 12.5,
              fontFamily: "inherit",
              fontWeight: active ? 700 : 400,
              background: active ? C.green : "transparent",
              color: active ? "#08130f" : C.mute,
              transition: "background .2s ease, color .2s ease",
            }}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function CardShell({ c, m, children }) {
  return (
    <div
      className="card-lift"
      style={{
        minHeight: m ? "auto" : 540,
        height: "100%",
        padding: m ? 18 : 28,
        borderRadius: 16,
        background: C.card,
        border: `1px solid ${C.line}`,
        color: C.text,
        display: "flex",
        flexDirection: "column",
        gap: m ? 16 : 20,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
          <span className="font-type" style={{ fontSize: m ? 20 : 23, fontWeight: 700 }}>{c.name}</span>
          <span style={{ fontSize: m ? 12.5 : 13.5, color: C.mute }}>{c.sub}</span>
        </div>
        <span style={{ fontSize: 12, color: c.tag === "Early access" ? C.green : C.mute, flexShrink: 0 }}>{c.tag}</span>
      </div>
      {children}
    </div>
  );
}

/* ---------- Assess ---------- */

function Assess({ c, m }) {
  const d = assessData;
  const [inst, setInst] = useState(d.defInst);
  const dsr = ((d.commitments + inst) / d.income) * 100;
  const status = dsr <= d.limitPct ? "approve" : dsr <= d.limitPct + 5 ? "refer" : "decline";
  const decColor = status === "approve" ? C.green : status === "refer" ? C.amber : C.red;
  const decText =
    status === "approve"
      ? `Approve, limit ${d.approveLimit}`
      : status === "refer"
      ? "Refer to a credit officer"
      : "Decline, ratio over policy limit";
  const barPct = Math.min(100, (dsr / 50) * 100);
  const marker = (d.limitPct / 50) * 100;
  const fs = m ? 12.5 : 13;

  const rows = [
    ["Verified monthly income", `USD ${d.income.toLocaleString()}`],
    ["Existing commitments", `USD ${d.commitments}`],
    ["Proposed instalment", `USD ${inst}`],
    ["Debt service ratio", `${dsr.toFixed(0)}% of ${d.limitPct}% limit`],
  ];

  return (
    <CardShell c={c} m={m}>
      <div style={{ flexGrow: 1, padding: m ? 14 : 20, borderRadius: 12, background: C.inner, border: `1px solid ${C.line2}`, display: "flex", flexDirection: "column", gap: 14, fontSize: fs }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontWeight: 600 }}>{d.ref}</span>
          <span style={{ color: C.mute, flexShrink: 0 }}>{d.time}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {rows.map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.line2}` }}>
              <span style={{ color: C.mute }}>{k}</span>
              <span style={{ fontFamily: '"IBM Plex Mono", monospace', flexShrink: 0 }}>{v}</span>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.mute }}>
            <span>Adjust proposed instalment</span>
            <span style={{ fontFamily: '"IBM Plex Mono", monospace', color: C.text }}>USD {inst}</span>
          </div>
          <DarkSlider value={inst} min={d.minInst} max={d.maxInst} onChange={setInst} />
          <div style={{ position: "relative", height: 8, borderRadius: 4, background: C.line2, overflow: "hidden" }}>
            <div style={{ width: `${barPct}%`, height: "100%", borderRadius: 4, background: decColor, transition: "width .15s ease, background .2s ease" }} />
          </div>
          <div style={{ position: "relative", height: 12, marginTop: -6 }}>
            <span style={{ position: "absolute", left: `${marker}%`, top: 0, fontSize: 10, color: C.mute, transform: "translateX(-50%)" }}>{d.limitPct}% limit</span>
          </div>
        </div>

        <div style={{ flexGrow: 1 }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "14px 16px", borderRadius: 10, background: "#15241f" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
            <span style={{ fontWeight: 700, color: decColor, transition: "color .2s ease" }}>{decText}</span>
            <span style={{ fontSize: 12, color: C.mute }}>{d.decisionSub}</span>
          </div>
          <span style={{ fontSize: 12, color: C.mute, flexShrink: 0 }}>{d.ruleset}</span>
        </div>
      </div>
    </CardShell>
  );
}

/* ---------- Watch ---------- */

function Watch({ c, m }) {
  const d = watchData;
  const [filter, setFilter] = useState("All");
  const [openId, setOpenId] = useState(null);
  const [resolved, setResolved] = useState({});
  const barColor = (tone) => (tone === "warn" ? C.amber : C.mute);

  const visible = d.signals.filter((s) => filter === "All" || s.cat === filter);
  const openCount = d.signals.filter((s) => !resolved[s.id]).length;
  const fs = m ? 12.5 : 13;

  return (
    <CardShell c={c} m={m}>
      <div style={{ flexGrow: 1, padding: m ? 14 : 20, borderRadius: 12, background: C.inner, border: `1px solid ${C.line2}`, display: "flex", flexDirection: "column", gap: 14, fontSize: fs }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 600 }}>{d.header}</span>
          <span style={{ color: C.green, fontSize: 12 }}>{openCount} open</span>
        </div>
        <Segmented options={d.filters} value={filter} onChange={setFilter} m={m} />

        <div className="no-bar" style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: m ? 340 : 268, overflowY: "auto", paddingRight: 2 }}>
          {visible.map((s) => {
            const isOpen = openId === s.id;
            const isRes = resolved[s.id];
            return (
              <div
                key={s.id}
                onClick={() => setOpenId(isOpen ? null : s.id)}
                style={{
                  display: "flex",
                  gap: 12,
                  padding: 14,
                  borderRadius: 10,
                  cursor: "pointer",
                  opacity: isRes ? 0.45 : 1,
                  background: s.tone === "warn" && !isRes ? "#1e1a14" : "#17202a",
                  border: `1px solid ${s.tone === "warn" && !isRes ? "#3a2f1e" : C.line}`,
                  transition: "opacity .25s ease, background .2s ease",
                }}
              >
                <span style={{ width: 6, flexShrink: 0, borderRadius: 4, background: isRes ? C.mute : barColor(s.tone) }} />
                <div style={{ display: "flex", flexDirection: "column", gap: 4, flexGrow: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontWeight: 600, textDecoration: isRes ? "line-through" : "none" }}>{s.t}</span>
                    <span style={{ fontSize: 10, color: C.mute, flexShrink: 0, marginTop: 2 }}>{isRes ? "Resolved" : s.cat}</span>
                  </div>
                  <span style={{ color: C.mute, lineHeight: 1.45 }}>{s.b}</span>
                  <div style={{ overflow: "hidden", maxHeight: isOpen ? 160 : 0, opacity: isOpen ? 1 : 0, transition: "max-height .35s ease, opacity .3s ease" }}>
                    <span style={{ display: "block", color: C.mute, lineHeight: 1.5, paddingTop: 8, borderTop: `1px solid ${C.line2}`, marginTop: 6 }}>{s.detail}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setResolved((r) => ({ ...r, [s.id]: !r[s.id] }));
                      }}
                      style={{ marginTop: 10, border: `1px solid ${C.green}`, background: "transparent", color: C.green, borderRadius: 7, padding: "6px 12px", fontSize: 12, fontFamily: "inherit", cursor: "pointer" }}
                    >
                      {isRes ? "Reopen signal" : "Mark resolved"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {visible.length === 0 && (
            <div style={{ color: C.mute, padding: "20px 0", textAlign: "center" }}>No signals in this category.</div>
          )}
        </div>
      </div>
    </CardShell>
  );
}

/* ---------- Forge ---------- */

function Forge({ c, m }) {
  const d = forgeData;
  const [ads, setAds] = useState(d.adapters);
  const [verifying, setVerifying] = useState(false);
  const [verifiedAt, setVerifiedAt] = useState("Signature verified");
  const active = ads.filter((a) => a.on).length;
  const fs = m ? 12.5 : 13;

  const toggle = (i) => setAds((prev) => prev.map((a, idx) => (idx === i ? { ...a, on: !a.on } : a)));
  const reverify = () => {
    if (verifying) return;
    setVerifying(true);
    setVerifiedAt("Verifying weights\u2026");
    setTimeout(() => {
      setVerifying(false);
      setVerifiedAt("Signature verified");
    }, 1500);
  };

  return (
    <CardShell c={c} m={m}>
      <div style={{ flexGrow: 1, padding: m ? 14 : 20, borderRadius: 12, background: C.inner, border: `1px solid ${C.line2}`, display: "flex", flexDirection: "column", gap: 14, fontSize: fs }}>
        <div style={{ display: "flex", flexDirection: m ? "column" : "row", justifyContent: "space-between", alignItems: m ? "stretch" : "center", padding: "14px 16px", borderRadius: 10, background: "#17202a", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
            <span style={{ fontWeight: 600 }}>{d.base}</span>
            <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 11, color: C.mute, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.fingerprint}</span>
          </div>
          <div style={{ display: "flex", flexDirection: m ? "row" : "column", justifyContent: "space-between", alignItems: m ? "center" : "flex-end", gap: m ? 10 : 6, flexShrink: 0 }}>
            <span style={{ fontSize: 12, color: verifying ? C.amber : C.green, display: "inline-flex", alignItems: "center", gap: 6 }}>
              {verifying && <span className="live-dot" style={{ background: C.amber }} />}
              {verifiedAt}
            </span>
            <button
              onClick={reverify}
              style={{ border: `1px solid ${C.line}`, background: "transparent", color: C.text, borderRadius: 7, padding: "5px 10px", fontSize: 11.5, fontFamily: "inherit", cursor: "pointer" }}
            >
              Re-verify
            </button>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.mute }}>
          <span>Adapters, tuned in this bank</span>
          <span>{active} of {ads.length} active</span>
        </div>

        <div style={{ display: "flex", flexDirection: "column" }}>
          {ads.map((a, i) => (
            <div key={a.name} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto auto", alignItems: "center", gap: 12, padding: "11px 0", borderBottom: i === ads.length - 1 ? "none" : `1px solid ${C.line2}`, opacity: a.on ? 1 : 0.5, transition: "opacity .2s ease" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
                <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 11, color: C.mute }}>{a.v}</span>
              </div>
              <span style={{ fontSize: 12, color: !a.on ? C.mute : a.tone === "ok" ? C.green : C.amber, textAlign: "right", minWidth: m ? 58 : 74 }}>
                {a.on ? a.status : "Disabled"}
              </span>
              <Switch
                checked={a.on}
                onCheckedChange={() => toggle(i)}
                className="data-[state=checked]:bg-[#2e7d68] data-[state=unchecked]:bg-white/15"
              />
            </div>
          ))}
        </div>

        <div style={{ flexGrow: 1 }} />
        <div style={{ padding: "12px 14px", borderRadius: 10, background: "#17202a", color: C.mute, lineHeight: 1.5 }}>{d.note}</div>
      </div>
    </CardShell>
  );
}

/* ---------- Provision ---------- */

function Provision({ c, m }) {
  const d = provisionData;
  const [scn, setScn] = useState("Base");
  const sc = d.scenarios.find((s) => s.name === scn) || d.scenarios[0];
  const f = sc.factor;

  const rows = d.rows.map((r) => ({ stage: r.stage, exp: r.exp, ecl: r.ecl * f, cov: r.cov * f }));
  const totalExp = d.rows.reduce((a, r) => a + r.exp, 0);
  const totalEcl = rows.reduce((a, r) => a + r.ecl, 0);
  const totalCov = (totalEcl / totalExp) * 100;
  const climate = d.scenarios.filter((s) => s.name !== "Base");
  const toneColor = { cream: "#ece7dc", amber: C.amber, red: C.red };
  const tblFS = m ? 11 : 13;
  const cols = "minmax(0,1.1fr) minmax(0,1fr) minmax(0,0.85fr) minmax(0,1fr)";

  return (
    <CardShell c={c} m={m}>
      <div style={{ flexGrow: 1, padding: m ? 14 : 20, borderRadius: 12, background: C.inner, border: `1px solid ${C.line2}`, display: "flex", flexDirection: "column", gap: 14, fontSize: m ? 12.5 : 13 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 600 }}>{d.header}</span>
          <span style={{ color: C.mute }}>{d.unit}</span>
        </div>

        <Segmented options={d.scenarios.map((s) => s.name)} value={scn} onChange={setScn} m={m} />

        <div style={{ display: "flex", flexDirection: "column", fontFamily: '"IBM Plex Mono", monospace', fontSize: tblFS }}>
          <div style={{ display: "grid", gridTemplateColumns: cols, gap: 6, padding: "8px 0", fontSize: m ? 10 : 11, color: C.mute, borderBottom: `1px solid ${C.line2}` }}>
            {d.cols.map((col, i) => (
              <span key={col} style={{ textAlign: i === 0 ? "left" : "right", overflow: "hidden", textOverflow: "ellipsis" }}>{col}</span>
            ))}
          </div>
          {rows.map((r) => (
            <div key={r.stage} style={{ display: "grid", gridTemplateColumns: cols, gap: 6, padding: "9px 0", borderBottom: `1px solid ${C.line2}` }}>
              <span style={{ textAlign: "left" }}>{r.stage}</span>
              <span style={{ textAlign: "right" }}>{money(r.exp)}</span>
              <span style={{ textAlign: "right", color: C.green, transition: "color .2s ease" }}>{money(r.ecl)}</span>
              <span style={{ textAlign: "right" }}>{r.cov.toFixed(2)}%</span>
            </div>
          ))}
          <div style={{ display: "grid", gridTemplateColumns: cols, gap: 6, padding: "9px 0", fontWeight: 700 }}>
            <span style={{ textAlign: "left" }}>Total</span>
            <span style={{ textAlign: "right" }}>{money(totalExp)}</span>
            <span style={{ textAlign: "right" }}>{money(totalEcl)}</span>
            <span style={{ textAlign: "right" }}>{totalCov.toFixed(2)}%</span>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 12, color: C.mute }}>Climate stress, ECL change against base</div>
          {climate.map((s) => {
            const w = ((s.factor - 1) / 0.22) * 100;
            const activeScn = s.name === scn;
            return (
              <div
                key={s.name}
                onClick={() => setScn(s.name)}
                style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", padding: "3px 6px", margin: "0 -6px", borderRadius: 8, background: activeScn ? "rgba(124,203,181,0.1)" : "transparent", transition: "background .2s ease" }}
              >
                <span style={{ width: m ? 74 : 100, flexShrink: 0, color: activeScn ? C.text : C.mute }}>{s.name}</span>
                <span style={{ flexGrow: 1, minWidth: 0, height: 8, borderRadius: 4, background: C.line2, overflow: "hidden" }}>
                  <span style={{ display: "block", width: `${w}%`, height: "100%", borderRadius: 4, background: toneColor[s.tone], boxShadow: activeScn ? `0 0 0 1px ${toneColor[s.tone]}` : "none", transition: "width .3s ease" }} />
                </span>
                <span style={{ width: 44, flexShrink: 0, textAlign: "right", fontFamily: '"IBM Plex Mono", monospace' }}>{s.delta}</span>
              </div>
            );
          })}
        </div>
      </div>
    </CardShell>
  );
}

const KINDS = { assess: Assess, watch: Watch, forge: Forge, provision: Provision };

export default function Screens() {
  const m = useMediaQuery("(max-width: 640px)");
  return (
    <Section id="screens" bg="var(--shell)" pt={m ? 90 : 150} pb={m ? 90 : 150}>
      <h2 className="font-type reveal" style={{ margin: "0 0 12px", maxWidth: 760, fontSize: "clamp(30px,4.4vw,58px)", fontWeight: 700, lineHeight: 1.05, letterSpacing: "-1.2px" }}>
        {screens.heading}
      </h2>
      <p className="reveal d1" style={{ margin: "0 0 44px", fontSize: 16, lineHeight: 1.5, color: "var(--ink-soft)" }}>
        Live previews — adjust an instalment, resolve a signal, toggle an adapter or run a climate scenario.
      </p>
      <div className="screens-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 24, alignItems: "stretch" }}>
        {screens.cards.map((c, i) => {
          const Comp = KINDS[c.kind];
          return (
            <div key={c.name} className={`reveal ${i % 2 ? "d1" : ""}`} style={{ minWidth: 0 }}>
              <Comp c={c} m={m} />
            </div>
          );
        })}
      </div>
      <style>{`
        @media (max-width: 880px) {
          .screens-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </Section>
  );
}
